/**
 * @Description 商场决策报告-商务条件
 */

import { FC, useEffect, useMemo, useState } from 'react';
import { beautifyThePrice, isNotEmpty } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import styles from './index.module.less';
import V2Table from '@/common/components/Data/V2Table';
import V2Title from '@/common/components/Feedback/V2Title';
import { Divider } from 'antd';
import { higherRentSettlementText } from '../../rentPresentation';

// 实付租金取概览详情接口 businessInfo.monthExpense；商务条件接口 monthExpense 仍按月度费用展示。

interface CommercialConditionProps {
  /**
   * 商务条件数据，由父级 MallDecisionReport 统一请求后下发。
   *
   * 【不要在本组件内部再请求 /decision/mallReport/businessInfo】
   * 请求层按 URL 去重并自动 cancel 先发出的那次请求，而子组件 effect 先于父组件执行，
   * 内部重复请求会被父级的请求 cancel，导致拿不到数据。详见父级 loadDetail 上的注释。
   */
  detail?: CommercialConditionDetail;
}

export interface CommercialConditionDetail {
  id?: number;
  monthExpense?: number | string;
  actualMonthlyRent?: number | string;
  leasePeriod?: number | string;
  deposit?: number | string;
  depositMonths?: number | string;
  decorationDeposit?: number | string;
  businessType?: number | string;
  monthlyRent?: number | string;
  deductionRate?: number | string;
  deductionAmount?: number | string;
  propertyManagementFee?: number | string;
  promotionFund?: number | string;
  monthlyRentAndFeeTotal?: number | string;
}

const BUSINESS_TYPE_TEXT: Record<string, string> = {
  '1': '纯租金',
  '2': '纯扣点',
  '3': '租金+扣点',
  '4': '二者取高',
};

/**
 * @Description 商场决策报告-商务条件
 */
const CommercialCondition: FC<CommercialConditionProps> = ({ detail }) => {
  const [tableKey, setTableKey] = useState<number>(0);

  const methods = useMethods({
    formatMoney(value: number | string) {
      if (!isNotEmpty(value)) return '-';
      return `${beautifyThePrice(value, ',', 0)}元/月`;
    },
    formatAmount(value: number | string) {
      if (!isNotEmpty(value)) return '-';
      return `${beautifyThePrice(value, ',', 0)}元`;
    },
    formatText(value: number | string | undefined, unit = '') {
      if (!isNotEmpty(value)) return '-';
      return `${value}${unit}`;
    },
    getBusinessType() {
      return BUSINESS_TYPE_TEXT[String(detail?.businessType)] || '-';
    },
    getDepositDescription() {
      return `另需缴纳可退押金 ${methods.formatAmount(detail?.deposit ?? '')}（租赁押金 ${methods.formatText(detail?.depositMonths, '个月')} + 装修押金 ${methods.formatAmount(detail?.decorationDeposit)}）。押金占用资金但期满可退，不计入回本测算。`;
    },
    getDeductionTitle() {
      return `扣点${isNotEmpty(detail?.deductionRate) ? `${detail?.deductionRate}%` : ''}`;
    },
    getHigherRentSettlement() {
      return higherRentSettlementText(detail, methods.formatMoney);
    },
    isHigherValue(value: number | string | undefined) {
      if (!isNotEmpty(detail?.monthlyRent) || !isNotEmpty(detail?.deductionAmount) || !isNotEmpty(value)) {
        return false;
      }
      const monthlyRent = Number(detail?.monthlyRent);
      const deductionAmount = Number(detail?.deductionAmount);
      if (!Number.isFinite(monthlyRent) || !Number.isFinite(deductionAmount) || monthlyRent === deductionAmount) {
        return false;
      }
      return Number(value) === Math.max(monthlyRent, deductionAmount);
    },
    renderMoney(value: number | string, className = '') {
      return <span className={className}>{methods.formatMoney(value)}</span>;
    },
    renderMonthlyRent(value: number | string) {
      const className = String(detail?.businessType) === '4' && methods.isHigherValue(value) ? 'c-ff8' : '';
      return methods.renderMoney(value, className);
    },
    renderDeductionAmount(value: number | string) {
      const className = String(detail?.businessType) === '4' && methods.isHigherValue(value) ? 'c-ff8' : '';
      return methods.renderMoney(value, className);
    },
    renderTotal(value: number | string) {
      return methods.renderMoney(value, 'c-009');
    },
    renderPropertyManagementFee(value: number | string) {
      return methods.renderMoney(value);
    },
    renderPromotionFund(value: number | string) {
      return methods.renderMoney(value);
    },
    getColumns() {
      const businessType = String(detail?.businessType);
      const rentColumn = {
        key: 'monthlyRent',
        title: <span className='c-666 fs-14'>月租金</span>,
        width: 180,
        render: methods.renderMonthlyRent,
      };
      const deductionColumn = {
        key: 'deductionAmount',
        title: <span className='c-666 fs-14'>{methods.getDeductionTitle()}</span>,
        width: 180,
        render: methods.renderDeductionAmount,
      };
      const propertyColumn = {
        key: 'propertyManagementFee',
        title: <span className='c-666 fs-14'>物业费</span>,
        width: 180,
        render: methods.renderPropertyManagementFee,
      };
      const promotionColumn = {
        key: 'promotionFund',
        title: <span className='c-666 fs-14'>推广基金</span>,
        width: 180,
        render: methods.renderPromotionFund,
      };
      const totalColumn = {
        key: 'monthlyRentAndFeeTotal',
        title: <span className='c-666 fs-14'>月度租金及费用合计</span>,
        render: methods.renderTotal,
      };
      if (businessType === '1') return [rentColumn, propertyColumn, promotionColumn, totalColumn];
      if (businessType === '2') return [deductionColumn, propertyColumn, promotionColumn, totalColumn];
      if (businessType === '3' || businessType === '4') return [rentColumn, deductionColumn, propertyColumn, promotionColumn, totalColumn];
      return [rentColumn, deductionColumn, propertyColumn, promotionColumn, totalColumn];
    },
    async loadTable() {
      return {
        dataSource: detail ? [detail] : [],
        count: detail ? 1 : 0,
      };
    },
  });
  const columns = useMemo(() => methods.getColumns(), [detail]);

  // detail 变化后需要重挂 V2Table，让其重新走 onFetch 取最新数据
  useEffect(() => {
    setTableKey((value) => value + 1);
  }, [detail]);

  return (
    <section
      className={styles.commercialCondition}
      data-report-anchor='business'
    >
      <V2Title
        type='H2'
        bold
      >
        <div className={styles.titCon}>
          <div className={styles.titImg}>
            <img src={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/mallDecision/icon_module_business@2x.png`} width='100%' height='100%' />
          </div>
          <div className='fs-18 bold c-222 ml-6'>
          商务条件
          </div>
        </div>
      </V2Title>
      <div className={styles.businessGrid}>
        <div className={styles.businessCard}>
          <img
            className={styles.businessIcon}
            src={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/mallDecision/icon_cost_01@2x.png`}
            alt='月度费用'
          />
          <div>
            <strong>{methods.formatMoney(detail?.monthExpense)}</strong>
            <span className='c-999 fs-12'>月度费用</span>
          </div>
        </div>
        <div className={styles.businessCard}>
          <img
            className={styles.businessIcon}
            src={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/mallDecision/icon_cost_02@2x.png`}
            alt='租期'
          />
          <div>
            <strong>{methods.formatText(detail?.leasePeriod, '个月')}</strong>
            <span className='c-999 fs-12'>租期(合同周期)</span>
          </div>
        </div>
        <div className={styles.businessCard}>
          <img
            className={styles.businessIcon}
            src={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/mallDecision/icon_cost_03@2x.png`}
            alt='押金'
          />
          <div>
            <strong>{methods.formatAmount(detail?.deposit)}</strong>
            <span className='c-999 fs-12'>押金</span>
          </div>
        </div>
        <div className={styles.businessCard}>
          <img
            className={styles.businessIcon}
            src={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/mallDecision/icon_cost_04@2x.png`}
            alt='商务类型'
          />
          <div>
            <strong>{methods.getBusinessType()}</strong>
            <span className='c-999 fs-12'>商务类型</span>
          </div>
        </div>
      </div>
      <div className={styles.detailCard}>
        <div className='fs-16 c-222 fw-600 mb-12'>费用明细</div>
        <V2Table
          key={tableKey}
          rowKey='id'
          defaultColumns={columns}
          onFetch={methods.loadTable}
          pagination={false}
          bordered
          hideColumnPlaceholder
          emptyRender
        />
        {methods.getHigherRentSettlement() && <div className={styles.rentSettlement}>{methods.getHigherRentSettlement()}</div>}
        <Divider dashed style={{
          marginTop: 16,
          marginBottom: 12
        }}/>
        <div className='fs-12 c-999'>{methods.getDepositDescription()}</div>
      </div>
    </section>
  );
};

export default CommercialCondition;
